import React, { useMemo } from 'react';
import { Button, Col, Descriptions, Row, Spin } from 'antd';
import { Document, Page } from 'react-pdf';
import { Entity } from '../../types';
import { BundledPages, FailedToLoadPage } from './components';

const WIDTH = 226;
const HEIGHT = 320;

type Props = {
  entity: Entity;
  entities: Entity[];
  i: number;
  entityRefs: any;
  file: string | null;
  numPages: number;
  setDetailEntity: (any: any) => any;
  expandedRow: number | null;
  setExpandedRow: any;
};

export const DocumentRow = ({
  entity,
  entities,
  i,
  entityRefs,
  file,
  numPages,
  setDetailEntity,
  expandedRow,
  setExpandedRow,
}: Props) => {
  const showAllPages = useMemo(() => expandedRow === i, [expandedRow, i]);

  return (
    <Row
      key={entity.id}
      ref={entityRefs[entity.id]}
      style={{ height: entities.length <= 2 ? '50%' : '', minHeight: '350px', width: '100%' }}>
      <Col
        xs={{ span: 16 }}
        xl={{ span: 18 }}
        className="flex justify-center items-center"
        style={{ borderBottom: '1px solid #cecece' }}>
        <div className="relative" style={{ width: showAllPages ? '100%' : 'auto', padding: '1em' }}>
          <div
            style={{
              width: showAllPages ? '100%' : WIDTH,
              minHeight: HEIGHT,
              overflowX: showAllPages ? 'auto' : 'visible',
              display: 'flex',
            }}>
            <div
              style={{
                width: showAllPages ? WIDTH * entity.length + 8 * entity.length + 50 : WIDTH,
                cursor: entity.length > 1 ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (entity.length > 1) {
                  if (expandedRow === i) {
                    setExpandedRow(null);
                  } else {
                    setExpandedRow(i);
                  }
                }
              }}>
              <Document
                file={file}
                loading={<Spin />}
                className={'flex justify-center items-center h-full pointer-events-none'}>
                {numPages > 0 ? (
                  <Page
                    className={(showAllPages ? 'mx-2' : '') + ' mb-1 border border-solid border-gray-400'}
                    height={HEIGHT}
                    width={WIDTH}
                    pageNumber={entity.start_page}
                    error={<FailedToLoadPage width={WIDTH} height={HEIGHT} />}
                  />
                ) : null}
                {showAllPages ? (
                  <>
                    {new Array(entity.length - 1)
                      .fill(entity.start_page + 1)
                      .map((index, i) => index + i)
                      .map(index => {
                        return (
                          <Page
                            key={index}
                            className="mx-2 mb-1 border border-solid border-gray-400"
                            pageNumber={index}
                            height={HEIGHT}
                            width={WIDTH}
                            error={<FailedToLoadPage width={WIDTH} height={HEIGHT} />}
                          />
                        );
                      })}
                  </>
                ) : null}
              </Document>
            </div>
          </div>
          {numPages > 0 && !showAllPages ? (
            <BundledPages width={12} docWidth={WIDTH} docHeight={HEIGHT} entity={entity} origin="row" />
          ) : null}
        </div>
      </Col>
      <Col
        xs={{ span: 8 }}
        xl={{ span: 6 }}
        className="p-4 flex flex-col justify-center items-center"
        style={{
          background: '#fff',
          borderLeft: '1px solid #dcdcdc',
          borderBottom: '1px solid #dcdcdc',
        }}>
        <div className="max-w-full overflow-x-scroll">
          <Descriptions style={{ marginTop: '16px', width: '270px' }} column={1} bordered size="small">
            <Descriptions.Item label="Document type">{entity.type}</Descriptions.Item>
            <Descriptions.Item label="Start page">{entity.start_page}</Descriptions.Item>
            <Descriptions.Item label="Page count">{entity.length}</Descriptions.Item>
            <Descriptions.Item label="Probability score">{entity.prob}</Descriptions.Item>
          </Descriptions>
        </div>
        <Button
          style={{ marginTop: '2em' }}
          onClick={() => {
            setDetailEntity({ entity: entity, index: i + 1 });
          }}>
          View document
        </Button>
      </Col>
    </Row>
  );
};
